import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { LayoutListItem } from '../../../models/api/query/query-layouts';
import { Action } from '../../../models/core/action';
import { BaseComponent } from '../../base/base.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { InputComponent } from '../../core/controls/input.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ProgressbarComponent } from '../../core/labels/progressbar.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { QUERY_LIST_PAGE_SIZE_OPTIONS } from '../query-list-page-size-options';

@Component({
  selector: 'app-layout-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableComponent,
    ColumnComponent,
    ActionButtonComponent,
    ButtonComponent,
    CheckboxComponent,
    InputComponent,
    ActionRowComponent,
    EmptyStateComponent,
    MenuComponent,
    ProgressbarComponent,
  ],
  template: `
    <div>
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-between sm:tw-items-center tw-gap-2 tw-mb-4"
      >
        <h2 class="tw-text-lg tw-font-semibold">Custom Layouts</h2>
        <div class="tw-flex tw-flex-wrap tw-gap-2">
          <app-button
            label="Create New Layout"
            icon="fa-light fa-plus"
            (click)="createLayout()"
          />
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
        <app-checkbox [control]="includePublicControl" label="Include Public" />
        <div
          class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-gap-2"
        >
          <app-input
            class="tw-w-full sm:tw-w-1/2 tw-min-w-0"
            [control]="fieldSearchControl"
            label="Search by Field"
            placeholder="Enter field name..."
          />
          <app-button
            class="tw-w-full sm:tw-w-auto tw-pb-2 tw-self-end"
            label="Search"
            icon="fa-light fa-search"
            (click)="searchByField()"
          />
          @if (hasFieldSearch) {
            <app-button
              class="tw-w-full sm:tw-w-auto tw-pb-2 tw-self-end"
              label="Reset"
              emphasis="Low"
              (click)="resetFieldSearch()"
            />
          }
        </div>
      </div>

      <div class="tw-flex tw-flex-row tw-w-full">
        @if (loading) {
          <app-empty-state
            height="300px"
            icon="fa-light fa-table-columns"
            label="Loading layouts"
            message="Retrieving custom layouts"
            class="tw-w-full"
          >
            <div class="tw-py-4">
              <app-progressbar />
            </div>
          </app-empty-state>
        } @else {
          <app-table
            class="tw-w-full"
            [dataSource]="layouts"
            [showRefresh]="true"
            (refresh)="loadLayouts(true)"
            [showFilter]="true"
            [showSelection]="true"
            [selectionKey]="'layoutId'"
            [isRowSelectable]="isLayoutSelectable"
            (selectedEvent)="onSelectionChange($event)"
            recordLabel="Layout"
            recordLabelPlural="Layouts"
            dataKey="layoutId"
            [pageSize]="pageSize"
            [pageSizeOptions]="pageSizeOptions"
            [persistPaginationSettings]="true"
            tableId="query-view-layouts"
          >
            <app-column name="name" label="Name" [sort]="true" />
            <app-column name="description" label="Description" />
            <app-column name="owner" label="Owner" [sort]="true" />
            <app-column
              name="createdDate"
              label="Created"
              format="d"
              [sort]="true"
            />
            <app-column
              name="modifiedDate"
              label="Modified"
              format="d"
              [sort]="true"
            />
            <app-column name="actions" label="Actions" [sort]="false">
              <ng-template #cell let-row>
                <span class="md:tw-hidden">
                  <app-action-button
                    [menu]="rowMenu"
                    [row]="row"
                    (click)="openRowMenu($event, row)"
                  />
                </span>
                <div class="tw-hidden md:tw-flex tw-gap-1">
                  @if (row.isOwner) {
                    <app-action-button
                      label="Edit"
                      icon="fa-light fa-edit"
                      (click)="editLayout(row)"
                    />
                    <app-action-button
                      label="Copy"
                      icon="fa-light fa-copy"
                      (click)="copyLayout(row)"
                    />
                    <app-action-button
                      label="Delete"
                      icon="fa-light fa-trash-can"
                      (click)="deleteLayout(row)"
                    />
                  } @else {
                    <app-action-button
                      label="View"
                      icon="fa-light fa-eye"
                      (click)="editLayout(row)"
                    />
                    <app-action-button
                      label="Copy"
                      icon="fa-light fa-copy"
                      (click)="copyLayout(row)"
                    />
                  }
                </div>
              </ng-template>
            </app-column>
          </app-table>
        }
      </div>

      <app-menu [items]="rowMenuItems" #rowMenu (click)="onRowAction($event)" />

      @if (selectedLayouts.length > 0) {
        <app-action-row class="tw-mt-4">
          <span left1>{{ selectedLayouts.length }} selected</span>
          <app-button
            right1
            label="Delete Selected"
            icon="fa-light fa-trash-can"
            emphasis="Low"
            (click)="deleteSelected()"
          />
        </app-action-row>
      }
    </div>
  `,
  styles: ``,
})
export class LayoutListComponent extends BaseComponent implements OnInit {
  @ViewChild('rowMenu') rowMenu: MenuComponent;

  createRequested = output<void>();
  editRequested = output<LayoutListItem>();
  copyRequested = output<LayoutListItem>();

  layouts: LayoutListItem[] = [];
  selectedLayouts: LayoutListItem[] = [];
  loading = false;
  pageSize = 10;
  pageSizeOptions = [...QUERY_LIST_PAGE_SIZE_OPTIONS];

  isLayoutSelectable = (layout: LayoutListItem): boolean => layout.isOwner;

  includePublicControl = new FormControl(false);
  fieldSearchControl = new FormControl('');

  /** Applied server-side field search; cleared on reset or empty search. */
  private appliedFieldSearch: string | null = null;

  rowMenuItems: Action[] = [];

  ownerRowActions: Action[] = [
    { label: 'Edit', key: 'edit' },
    { label: 'Copy', key: 'copy' },
    { label: 'Delete', key: 'delete' },
  ];

  viewerRowActions: Action[] = [
    { label: 'View', key: 'view' },
    { label: 'Copy', key: 'copy' },
  ];

  get hasFieldSearch(): boolean {
    return !!this.appliedFieldSearch;
  }

  async ngOnInit(): Promise<void> {
    this.dispose.subs = this.includePublicControl.valueChanges.subscribe(() => {
      void this.loadLayouts(false);
    });

    await this.loadLayouts(true);
  }

  searchByField(): void {
    const term = this.fieldSearchControl.value?.trim() ?? '';
    this.appliedFieldSearch = term.length > 0 ? term : null;
    void this.loadLayouts(false);
  }

  resetFieldSearch(): void {
    this.fieldSearchControl.setValue('');
    this.appliedFieldSearch = null;
    void this.loadLayouts(false);
  }

  async loadLayouts(showLoading = true): Promise<void> {
    if (showLoading) {
      this.loading = true;
    }
    try {
      const response = await this.api.query.getLayouts({
        includePublic: this.includePublicControl.value ?? false,
        ...(this.appliedFieldSearch
          ? { search: this.appliedFieldSearch, searchField: 'Fields' }
          : {}),
      });
      this.layouts = response.items.map((item) => ({
        ...item,
        modifiedDate: item.modifiedDate ?? item.createdDate,
      }));
      this.selectedLayouts = [];
    } finally {
      this.loading = false;
    }
  }

  createLayout(): void {
    this.createRequested.emit();
  }

  editLayout(layout: LayoutListItem): void {
    this.editRequested.emit(layout);
  }

  deleteLayout(layout: LayoutListItem): void {
    this.confirm.show({
      label: 'Delete Layout',
      message: `Delete layout "${layout.name}"? This action cannot be undone.`,
      confirm: async () => {
        const response = await this.api.query.deleteLayout(layout.layoutId);
        if (response.success) {
          await this.loadLayouts();
        }
      },
    });
  }

  copyLayout(layout: LayoutListItem): void {
    this.copyRequested.emit(layout);
  }

  openRowMenu(event: Event, row: LayoutListItem): void {
    const source = row.isOwner ? this.ownerRowActions : this.viewerRowActions;
    this.rowMenuItems = source.map((a) => ({ label: a.label, key: a.key }));
    this.rowMenu.items = this.rowMenuItems;
    this.rowMenu.toggle(event, row);
  }

  onRowAction(action: Action): void {
    const layout = (this.rowMenu?.data ?? action.data) as LayoutListItem;
    if (!layout) return;

    switch (action.key) {
      case 'edit':
      case 'view':
        this.editLayout(layout);
        break;
      case 'copy':
        this.copyLayout(layout);
        break;
      case 'delete':
        this.deleteLayout(layout);
        break;
    }
  }

  onSelectionChange(selected: LayoutListItem[]): void {
    this.selectedLayouts = selected;
  }

  deleteSelected(): void {
    this.confirm.show({
      label: 'Delete Layouts',
      message: `Delete ${this.selectedLayouts.length} selected layouts? This action cannot be undone.`,
      confirm: async () => {
        for (const layout of this.selectedLayouts) {
          await this.api.query.deleteLayout(layout.layoutId);
        }
        this.selectedLayouts = [];
        await this.loadLayouts();
      },
    });
  }
}
